/* World Map — Leaflet integration for Media.co.uk OOH coverage.
   Per-product markers with auto-clustering at low zoom + individual pins at high zoom.

   Scale model (built to grow from ~1k to several thousand placements):
   - Data is paged at 250 products/page (Shopify's paginate cap) via the
     /collections/<handle>?view=mapdata&page=N endpoint (templates/collection.mapdata.liquid).
   - Page 1 is inlined for instant first paint; remaining pages are fetched with a
     bounded concurrency POOL (not all-at-once) and rendered INCREMENTALLY as they
     arrive, so the map paints fast and never fires a request storm.
   - markercluster keeps thousands of markers performant (individual pins only
     materialise past zoom 13). */
(function () {
  'use strict';

  // Max simultaneous page fetches. Browsers cap ~6 conns/host; staying under that
  // keeps the map responsive whether there are 3 pages or 40.
  var MAX_CONCURRENCY = 5;

  // City lat/lng fallback for any product missing per-product geocoding.
  // (Augmented at runtime by a dynamic lookup built from products that DO carry
  // their own coordinates — see buildCityGeo — so this static list never needs
  // to grow as new cities are added.)
  var CITY_COORDS = {
    'Dubai': [25.2048, 55.2708],
    'Doha': [25.2854, 51.5310],
    'Riyadh': [24.7136, 46.6753],
    'Amman': [31.9454, 35.9284],
    'Abu Dhabi': [24.4539, 54.3773],
    'Beirut': [33.8938, 35.5018],
    'Dammam, Khobar': [26.4207, 50.0888],
    'Dammam': [26.4207, 50.0888],
    'Khobar': [26.2172, 50.1971],
    'Jeddah': [21.4858, 39.1925],
    'Milan': [45.4642, 9.1900],
    'Muscat': [23.5859, 58.4059],
    'London': [51.5074, -0.1278],
    'Casablanca': [33.5731, -7.5898],
    'Algiers': [36.7538, 3.0588],
    'Tunis': [36.8065, 10.1815],
    'Rome': [41.9028, 12.4964],
    'Medina Madinah': [24.5247, 39.5692],
    'Medina': [24.5247, 39.5692],
    'Madinah': [24.5247, 39.5692],
    'Kuwait City': [29.3759, 47.9774],
    'Sharjah': [25.3463, 55.4209],
    'Rabat': [34.0209, -6.8417],
    'Al Ain': [24.2075, 55.7447],
    // Cyprus
    'Nicosia': [35.1856, 33.3823],
    'Limassol': [34.7071, 33.0226],
    'Larnaca': [34.9182, 33.6233],
    'Paphos': [34.7720, 32.4297],
    // Malaysia (incl. the multi-location label values used on national-network products)
    'Kuala Lumpur': [3.1390, 101.6869],
    'Kuala Lumpur, nationwide Malaysia': [3.1390, 101.6869],
    'Klang Valley': [3.0738, 101.5183],
    'Klang Valley, Malaysia': [3.0738, 101.5183],
    'Penang': [5.4141, 100.3288],
    'Johor Bahru': [1.4927, 103.7414],
    // Greece (incl. the multi-city label value)
    'Athens': [37.9838, 23.7275],
    'Thessaloniki': [40.6401, 22.9444],
    'Athens, Thessaloniki, major Greek cities': [37.9838, 23.7275],
    // South Africa towns lacking per-product coords (no coded sibling)
    'Barberton': [-25.7882, 31.0501],
    'Germiston': [-26.2185, 28.1670],
    'KaNyamazane': [-25.4500, 31.1833],
    'South Beach': [-29.8639, 31.0397],
    // Pakistan
    'Karachi': [24.8607, 67.0011],
    'Islamabad': [33.6844, 73.0479],
    'Lahore': [31.5204, 74.3587],
    'Faisalabad': [31.4504, 73.1350],
    'Rawalpindi': [33.5651, 73.0169],
    // Indonesia
    'Jakarta': [-6.2088, 106.8456],
    'Bogor': [-6.5950, 106.8166],
    'Semarang': [-6.9932, 110.4203],
    'Palembang': [-2.9761, 104.7754],
    'Yogyakarta': [-7.7956, 110.3695],
    'Surabaya': [-7.2575, 112.7521],
    'Bandung': [-6.9175, 107.6191],
    'Medan': [3.5952, 98.6722],
    // Georgia (country)
    'Tbilisi': [41.7151, 44.8271],
    'Batumi': [41.6168, 41.6367],
    'Kutaisi': [42.2679, 42.7180],
    'Rustavi': [41.5495, 45.0028],
    // Singapore (city-state)
    'Singapore': [1.3521, 103.8198],
    // India — Chhattisgarh cluster + majors
    'Raipur': [21.2514, 81.6296],
    'Bilaspur': [22.0797, 82.1409],
    'Bhilai': [21.1938, 81.3509],
    'Durg': [21.1904, 81.2849],
    'Korba': [22.3595, 82.7501],
    'Raigarh': [21.8974, 83.3950],
    'Jagdalpur': [19.0748, 82.0037],
    'Ambikapur': [23.1206, 83.1959],
    // Austria — regional cities
    'Wien': [48.2082, 16.3738],
    'Vienna': [48.2082, 16.3738],
    'Graz': [47.0707, 15.4395],
    'Linz': [48.3069, 14.2858],
    'Salzburg': [47.8095, 13.0550],
    'Innsbruck': [47.2692, 11.4041],
    'Klagenfurt': [46.6247, 14.3050],
    'Villach': [46.6111, 13.8558],
    'Dornbirn': [47.4125, 9.7417],
    'Wels': [48.1575, 14.0289],
    'Sankt Pölten': [48.2047, 15.6256],
    'Wiener Neustadt': [47.8149, 16.2408],
    'Feldkirch': [47.2382, 9.5988],
    'Bregenz': [47.5031, 9.7471],
    'Wolfsberg': [46.8406, 14.8455],
    'Leoben': [47.3809, 15.0947],
    'Kufstein': [47.5836, 12.1698],
    // Indonesia metro
    'Jabodetabek': [-6.2088, 106.8456],
    // New Zealand
    'Auckland': [-36.8485, 174.7633],
    'Wellington': [-41.2865, 174.7762],
    'Christchurch': [-43.5321, 172.6362],
    'New Plymouth': [-39.0556, 174.0752],
    // Canada
    'Toronto': [43.6532, -79.3832],
    'Montreal': [45.5019, -73.5674],
    'Vancouver': [49.2827, -123.1207],
    'Ottawa': [45.4215, -75.6972],
    // Ukraine
    'Kyiv': [50.4501, 30.5234],
    'Kharkiv': [49.9935, 36.2304],
    'Odesa': [46.4825, 30.7233],
    // Brazil
    'São Paulo': [-23.5558, -46.6396],
    'Rio de Janeiro': [-22.9068, -43.1729],
    'Brasília': [-15.7939, -47.8828],
    // Cameroon
    'Douala': [4.0511, 9.7679],
    'Yaoundé': [3.8480, 11.5021],
    // Trinidad and Tobago
    'Port of Spain': [10.6596, -61.5019],
    'San Fernando': [10.2799, -61.4589],
    'Chaguanas': [10.5168, -61.4114],
    // Spain
    'Madrid': [40.4168, -3.7038],
    // Belgium, Angola, Colombia, Central America & Caribbean
    'Brussels': [50.8503, 4.3517],
    'Luanda': [-8.839, 13.2894],
    'Bogotá': [4.711, -74.0721],
    'San José': [9.9281, -84.0907],
    'Belize City': [17.5046, -88.1962],
    'Nassau': [25.0443, -77.3504],
    'Bridgetown': [13.1132, -59.5988],
    'Castries': [14.0101, -60.9875],
    "St George's": [12.0561, -61.7488],
    "St John's": [17.1274, -61.8468],
    'George Town': [19.2869, -81.3674],
    'Kralendijk': [12.15, -68.2667],
    'Philipsburg': [18.0261, -63.0453],
    // Puerto Rico
    'San Juan': [18.4655, -66.1057],
    'Bayamón': [18.3985, -66.1557],
    'Ponce': [18.0111, -66.6141],
    // Bulgaria
    'Sofia': [42.6977, 23.3219],
    'Plovdiv': [42.1354, 24.7453],
    'Varna': [43.2141, 27.9147],
    // Albania
    'Tirana': [41.3275, 19.8187],
    'Durres': [41.3231, 19.4414],
    // Czechia, Slovakia, Vietnam
    'Prague': [50.0755, 14.4378],
    'Brno': [49.1951, 16.6068],
    'Ostrava': [49.8209, 18.2625],
    'Plzen': [49.7384, 13.3736],
    'Klatovy': [49.3956, 13.2951],
    'Bratislava': [48.1486, 17.1077],
    'Kosice': [48.7164, 21.2611],
    'Hanoi': [21.0285, 105.8542],
    'Ho Chi Minh City': [10.8231, 106.6297],
    'Da Nang': [16.0544, 108.2022],
    'Can Tho': [10.0452, 105.7469],
    'Phu Tho': [21.3227, 105.4019],
    'Hai Phong': [20.8449, 106.6881],
    'Nha Trang': [12.2388, 109.1967],
    'Dong Nai': [10.9574, 106.8426],
    'Nghe An': [18.6796, 105.6813],
    'Binh Duong': [11.3254, 106.477],
    'Quang Ninh': [21.0064, 107.2925],
    'Hue': [16.4637, 107.5909],
    'Thanh Hoa': [19.8067, 105.7852],
    'Nam Dinh': [20.4388, 106.1621],
    'Bac Ninh': [21.1861, 106.0763],
    'Hai Duong': [20.9373, 106.3146],
    'Plzeň': [49.7384, 13.3736],
    'Hodonín': [48.8489, 17.1325],
    'Zlín': [49.2265, 17.6689],
    'Mladá Boleslav': [50.4113, 14.9032],
    // Kosovo, Venezuela, Argentina
    'Prishtina': [42.6629, 21.1655],
    'Pristina': [42.6629, 21.1655],
    'Prizren': [42.2139, 20.7397],
    'Caracas': [10.4806, -66.9036],
    'Maracaibo': [10.6545, -71.6406],
    'Valencia': [10.162, -68.0077],
    'Buenos Aires': [-34.6037, -58.3816],
    'Vicente López': [-34.5256, -58.4797],
    // Uganda, Malta
    'Kampala': [0.3476, 32.5825],
    'Entebbe': [0.0512, 32.4637],
    'Gulu': [2.7746, 32.299],
    'Valletta': [35.8989, 14.5146],
    'Sliema': [35.9122, 14.5042],
    'Birkirkara': [35.8972, 14.4611],
    // South Korea
    'Seoul': [37.5665, 126.978],
    'Busan': [35.1796, 129.0756],
    'Incheon': [37.4563, 126.7052],
    'Daegu': [35.8714, 128.6014],
    // Finland, Iceland, Japan
    'Helsinki': [60.1699, 24.9384],
    'Espoo': [60.2055, 24.6559],
    'Reykjavik': [64.1466, -21.9426],
    'Keflavik': [64.0049, -22.5624],
    'Keflavík': [64.0049, -22.5624],
    'Tokyo': [35.6762, 139.6503],
    'Osaka': [34.6937, 135.5023],
    'Nagoya': [35.1815, 136.9066],
    'Fukuoka': [33.5904, 130.4017],
    // North Macedonia
    'Skopje': [41.9973, 21.4280],
    'Bitola': [41.0311, 21.3403],
    'Kumanovo': [42.1322, 21.7144],
    // Dominica
    'Roseau': [15.3017, -61.3881],
    'Portsmouth': [15.5850, -61.4620],
    'Canefield': [15.3369, -61.3922],
    'Jimmit': [15.3719, -61.3853],
    'Marigot': [15.5333, -61.2833]
  };

  // Country centroids — final fallback so EVERY placement plots near its
  // country even when the city is unknown and has no explicit coordinates.
  // Keys match the "Country: <name>" tag values (full names + the two legacy
  // abbreviations that still appear in older data).
  var COUNTRY_COORDS = {
    'United Arab Emirates': [24.0, 54.0], 'UAE': [24.0, 54.0],
    'Saudi Arabia': [23.885, 45.079], 'Qatar': [25.3548, 51.1839],
    'Kuwait': [29.3117, 47.4818], 'Bahrain': [26.0667, 50.5577],
    'Oman': [21.4735, 55.9754], 'Jordan': [31.24, 36.51],
    'Lebanon': [33.8547, 35.8623], 'Egypt': [26.8206, 30.8025],
    'Morocco': [31.7917, -7.0926], 'Algeria': [28.0339, 1.6596],
    'Tunisia': [33.8869, 9.5375], 'United Kingdom': [54.0, -2.5], 'UK': [54.0, -2.5],
    'United States': [39.0, -98.0], 'Italy': [41.8719, 12.5674],
    'France': [46.2276, 2.2137], 'Germany': [51.1657, 10.4515],
    'Spain': [40.4637, -3.7492], 'Portugal': [39.3999, -8.2245],
    'Netherlands': [52.1326, 5.2913], 'Austria': [47.5162, 14.5501],
    'Hungary': [47.1625, 19.5033], 'Monaco': [43.7384, 7.4246],
    'Gibraltar': [36.1408, -5.3536], 'Denmark': [56.2639, 9.5018],
    'Moldova': [47.4116, 28.3699], 'Croatia': [45.1, 15.2],
    'Greece': [39.0742, 21.8243], 'Cyprus': [35.1264, 33.4299],
    'Estonia': [58.5953, 25.0136], 'Latvia': [56.8796, 24.6032],
    'Lithuania': [55.1694, 23.8813], 'Bosnia and Herzegovina': [43.9159, 17.6791],
    'Albania': [41.1533, 20.1683], 'Bulgaria': [42.7339, 25.4858],
    'Serbia': [44.0165, 21.0059], 'Turkey': [38.9637, 35.2433],
    'Romania': [45.9432, 24.9668], 'Ukraine': [48.3794, 31.1656],
    'Georgia': [42.3154, 43.3569], 'South Africa': [-30.5595, 22.9375],
    'Botswana': [-22.3285, 24.6849], 'Kenya': [-0.0236, 37.9062],
    'Nigeria': [9.0820, 8.6753], 'Senegal': [14.4974, -14.4524],
    "Côte d'Ivoire": [7.5400, -5.5471], 'Ghana': [7.9465, -1.0232],
    'Zimbabwe': [-19.0154, 29.1549], 'Ethiopia': [9.1450, 40.4897],
    'Tanzania': [-6.3690, 34.8888], 'Cameroon': [7.3697, 12.3547],
    'Zambia': [-13.1339, 27.8493], 'Argentina': [-38.4161, -63.6167],
    'Brazil': [-14.2350, -51.9253], 'Canada': [56.1304, -106.3468],
    'Jamaica': [18.1096, -77.2975], 'India': [20.5937, 78.9629],
    'Pakistan': [30.3753, 69.3451], 'Nepal': [28.3949, 84.1240],
    'Bangladesh': [23.6850, 90.3563], 'Malaysia': [4.2105, 101.9758],
    'Singapore': [1.3521, 103.8198], 'Indonesia': [-2.5, 118.0],
    'Philippines': [12.8797, 121.7740], 'Australia': [-25.2744, 133.7751],
    'New Zealand': [-41.5, 172.5], 'Hong Kong': [22.3193, 114.1694],
    'Dominica': [15.4150, -61.3710],
    'Trinidad and Tobago': [10.6918, -61.2225],
    'North Macedonia': [41.6086, 21.7453],
    'Puerto Rico': [18.2208, -66.5901],
    'Czechia': [49.8175, 15.473],
    'Slovakia': [48.669, 19.699],
    'Vietnam': [14.0583, 108.2772],
    'Kosovo': [42.6026, 20.903],
    'Venezuela': [6.4238, -66.5897],
    'Uganda': [1.3733, 32.2903],
    'Malta': [35.9375, 14.3754],
    'South Korea': [35.9078, 127.7669],
    'Finland': [61.9241, 25.7482],
    'Iceland': [64.9631, -19.0208],
    'Japan': [36.2048, 138.2529],
    'Belgium': [50.5039, 4.4699],
    'Angola': [-11.2027, 17.8739],
    'Colombia': [4.5709, -74.2973],
    'Costa Rica': [9.7489, -83.7534],
    'Belize': [17.1899, -88.4976],
    'Bahamas': [25.0343, -77.3963],
    'Barbados': [13.1939, -59.5432],
    'Saint Lucia': [13.9094, -60.9789],
    'Grenada': [12.1165, -61.679],
    'Antigua and Barbuda': [17.0608, -61.7964],
    'Cayman Islands': [19.3133, -81.2546],
    'Bonaire': [12.2019, -68.2624],
    'Sint Maarten': [18.0425, -63.0548]
  };

  function escapeHtml(s) {
    if (s == null) return '';
    return String(s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  // Deterministic small jitter so products without per-product geocoding
  // don't stack at the city centroid.
  function jitterFromHandle(handle, lat, lng, radiusKm) {
    radiusKm = radiusKm || 2.5;
    var h = 0;
    for (var i = 0; i < handle.length; i++) h = (h * 31 + handle.charCodeAt(i)) | 0;
    var a = ((h >>> 0) % 10000) / 10000;
    var b = (((h * 7) >>> 0) % 10000) / 10000;
    var angle = a * 2 * Math.PI;
    var dist = b * radiusKm;
    var latOff = (dist / 111) * Math.cos(angle);
    var lngOff = (dist / (111 * Math.max(0.1, Math.cos(lat * Math.PI / 180)))) * Math.sin(angle);
    return [lat + latOff, lng + lngOff];
  }

  function parseJSON(t) { try { return JSON.parse(t); } catch (e) { return []; } }

  // Fold any product that carries its own lat/lng into the per-city centroid
  // lookup, so coord-less siblings in the same city can still plot. Updates in
  // place as new batches stream in.
  function buildCityGeo(products, cityGeo) {
    products.forEach(function (p) {
      if (!p.c || cityGeo[p.c]) return;
      if (p.la == null || p.lo == null || p.la === '' || p.lo === '') return;
      var la = Number(p.la), lo = Number(p.lo);
      if (!isNaN(la) && !isNaN(lo)) cityGeo[p.c] = [la, lo];
    });
  }

  function computeCoord(p, cityGeo) {
    var key = p.u || p.t || '';
    var hasGeo = (p.la != null && p.lo != null && p.la !== '' && p.lo !== '');
    if (hasGeo) {
      var lat = Number(p.la), lng = Number(p.lo);
      if (!isNaN(lat) && !isNaN(lng)) return jitterFromHandle(key, lat, lng, 0.7);
    }
    if (p.c && CITY_COORDS[p.c]) return jitterFromHandle(key, CITY_COORDS[p.c][0], CITY_COORDS[p.c][1], 2.5);
    if (p.c && cityGeo[p.c]) return jitterFromHandle(key, cityGeo[p.c][0], cityGeo[p.c][1], 2.5);
    if (p.co && COUNTRY_COORDS[p.co]) return jitterFromHandle(key, COUNTRY_COORDS[p.co][0], COUNTRY_COORDS[p.co][1], 3.2);
    return null;
  }

  // Bounded-concurrency page loader. Fetches pages [fromPage..toPage], at most
  // MAX_CONCURRENCY in flight, invoking onBatch(productsArray) as each resolves.
  // Returns a promise that settles when all pages are done. Never rejects — a
  // failed page is skipped so the rest of the map still loads.
  function fetchPagesPooled(source, fromPage, toPage, onBatch) {
    var next = fromPage;
    var active = 0;
    return new Promise(function (resolve) {
      function pump() {
        if (next > toPage && active === 0) { resolve(); return; }
        while (active < MAX_CONCURRENCY && next <= toPage) {
          var pg = next++;
          active++;
          fetch('/collections/' + encodeURIComponent(source) + '?view=mapdata&page=' + pg)
            .then(function (r) { return r.ok ? r.text() : '[]'; })
            .then(function (t) { var arr = parseJSON(t); if (arr && arr.length) onBatch(arr); })
            .catch(function () { /* skip this page */ })
            .then(function () { active--; pump(); });
        }
      }
      pump();
    });
  }

  function bootMap(root) {
    if (typeof L === 'undefined' || !L.markerClusterGroup) {
      setTimeout(function () { bootMap(root); }, 80);
      return;
    }
    if (root.dataset.wmBooted === '1') return;
    root.dataset.wmBooted = '1';

    var canvas = root.querySelector('[data-world-map-canvas]');
    var dataNode = root.querySelector('[data-world-map-data]');
    if (!canvas || !dataNode) return;

    var inlineProducts = parseJSON(dataNode.textContent);
    var source = root.dataset.source || 'ooh';
    var pages = parseInt(root.dataset.pages || '1', 10) || 1;
    // Backstop against a runaway pages value (e.g. tens of thousands of products):
    // cap the fetch fan-out and report it rather than firing unbounded requests.
    var MAX_PAGES = 200; // 200 * 250 = 50k placements
    if (pages > MAX_PAGES) {
      try { console.warn('[world-map] capping at', MAX_PAGES, 'pages of', pages); } catch (e) {}
      pages = MAX_PAGES;
    }

    var ctx = createMap(root, canvas);
    if (!ctx) return;

    // Instant first paint from the inlined page 1.
    addBatch(ctx, inlineProducts);
    firstPaint(ctx, root);

    // Stream the remaining pages in, bounded concurrency, rendering as they land.
    if (pages > 1) {
      fetchPagesPooled(source, 2, pages, function (batch) {
        addBatch(ctx, batch);
        updateMeta(ctx, root);
        // Only widen the view to fit new markers if the user hasn't interacted yet.
        if (!ctx.userMoved) fitToData(ctx, root);
      });
    }
  }

  function createMap(root, canvas) {
    var map = L.map(canvas, {
      zoomControl: true,
      scrollWheelZoom: false,
      worldCopyJump: true,
      attributionControl: false
    });

    // Base layer only, no labels. Esri World Light Gray Base: monochrome, no place
    // names, no API key. CARTO's free basemap tiles started shipping an
    // "API KEY REQUIRED" watermark on 2026-09-08 and every map on the site showed
    // it; Esri's canvas tiles need attribution, so the control is on with the
    // Leaflet prefix removed. Geography (coastlines, borders) still reads clearly
    // under the markers.
    L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Base/MapServer/tile/{z}/{y}/{x}', {
      attribution: 'Tiles &copy; Esri',
      maxZoom: 16,
      minZoom: 2
    }).addTo(map);
    L.control.attribution({ prefix: false, position: 'bottomright' }).addTo(map);

    var cluster = L.markerClusterGroup({
      showCoverageOnHover: false,
      spiderfyOnMaxZoom: false,
      zoomToBoundsOnClick: false,
      removeOutsideVisibleBounds: true,
      chunkedLoading: true,
      disableClusteringAtZoom: 13,
      maxClusterRadius: 60,
      iconCreateFunction: function (clst) {
        var n = clst.getChildCount();
        var size = n >= 20 ? 'large' : (n >= 10 ? 'medium' : 'small');
        return L.divIcon({
          html: '<span class="world-map-marker" data-size="' + size + '" aria-label="' + n + ' placements">' + n + '</span>',
          className: '',
          iconSize: [22, 22],
          iconAnchor: [11, 11]
        });
      }
    });

    cluster.on('clusterclick', function (e) {
      var center = e.layer.getLatLng();
      var bounds = e.layer.getBounds();
      var children = e.layer.getAllChildMarkers();
      var items = children.map(function (m) { return m._product; }).filter(Boolean);
      if (!items.length) return;

      var citySet = {};
      items.forEach(function (p) { if (p.c) citySet[p.c] = true; });
      var cityKeys = Object.keys(citySet);
      var country = items[0].co || '';
      var heading;
      if (cityKeys.length === 1) {
        heading = '<span class="wm-popup-eyebrow">' + escapeHtml(country) + '</span>' +
                  '<h3 class="wm-popup-title">' + escapeHtml(cityKeys[0]) + '</h3>';
      } else {
        heading = '<span class="wm-popup-eyebrow">' + escapeHtml(country) + '</span>' +
                  '<h3 class="wm-popup-title">' + cityKeys.length + ' cities</h3>';
      }

      var listHtml = items.slice(0, 30).map(function (p) {
        var fmt = p.f ? '<span class="wm-fmt">' + escapeHtml(p.f) + (p.c ? ' · ' + escapeHtml(p.c) : '') + '</span>' : (p.c ? '<span class="wm-fmt">' + escapeHtml(p.c) + '</span>' : '');
        return '<li><a href="' + escapeHtml(p.u) + '">' + escapeHtml(p.t) + fmt + '</a></li>';
      }).join('');
      var moreNote = items.length > 30 ? '<p class="wm-popup-count" style="margin-top:8px;">+ ' + (items.length - 30) + ' more</p>' : '';

      var html = heading +
        '<p class="wm-popup-count">' + items.length + ' placement' + (items.length === 1 ? '' : 's') + ' available</p>' +
        '<ul class="wm-popup-list">' + listHtml + '</ul>' +
        moreNote;

      var openPopup = function () {
        L.popup({ maxWidth: 320, minWidth: 240, closeButton: true, autoPan: true })
          .setLatLng(center)
          .setContent(html)
          .openOn(map);
      };
      if (bounds.isValid()) {
        map.flyToBounds(bounds, { padding: [60, 60], maxZoom: 13, duration: 0.55 });
        map.once('moveend', openPopup);
        setTimeout(function () { if (!map._popup) openPopup(); }, 900);
      } else {
        openPopup();
      }
    });

    map.addLayer(cluster);

    var PIN_SVG =
      '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 64" aria-hidden="true">' +
        '<path class="pin-body" fill="#0A0A0A" d="M24 0 C10.745 0 0 10.745 0 24 C0 37.255 10.745 48 24 60 C37.255 48 48 37.255 48 24 C48 10.745 37.255 0 24 0 Z"/>' +
        '<g class="pin-mark" fill="#FAFAFA">' +
          '<rect x="12" y="13" width="4" height="22" rx="2"/>' +
          '<rect x="22" y="13" width="4" height="22" rx="2"/>' +
          '<rect x="32" y="13" width="4" height="22" rx="2"/>' +
        '</g>' +
      '</svg>';

    var pinIcon = L.divIcon({
      className: 'world-map-pin',
      html: PIN_SVG,
      iconSize:    [24, 32],
      iconAnchor:  [12, 31],
      popupAnchor: [0,  -28]
    });

    // Resize the map if its container becomes visible later
    if ('ResizeObserver' in window) {
      new ResizeObserver(function () { map.invalidateSize(); }).observe(canvas);
    }

    var ctx = {
      map: map,
      cluster: cluster,
      pinIcon: pinIcon,
      cityGeo: {},
      cities: {},
      countries: {},
      count: 0,
      minPrice: 0,
      fitted: false,
      userMoved: false
    };

    // Stop auto-fitting to incoming batches once the user takes control.
    map.on('zoomstart dragstart', function () { ctx.userMoved = true; });

    return ctx;
  }

  // Add a batch of products to the map. Idempotent per call; safe to call many
  // times as pages stream in.
  function addBatch(ctx, products) {
    if (!products || !products.length) return;
    buildCityGeo(products, ctx.cityGeo);
    var markers = [];
    for (var i = 0; i < products.length; i++) {
      var p = products[i];
      // Track the catalogue-wide minimum price across the FULL set (even products
      // without coordinates), so the "from" hero stat is the real floor, uncapped.
      var prc = Number(p.pr);
      if (prc > 0 && (ctx.minPrice === 0 || prc < ctx.minPrice)) ctx.minPrice = prc;
      var coord = computeCoord(p, ctx.cityGeo);
      if (!coord) continue;
      var m = L.marker(coord, { icon: ctx.pinIcon, riseOnHover: true });
      m._product = p;
      var fmt = p.f ? '<p class="wm-pin-format">' + escapeHtml(p.f) + '</p>' : '';
      var html = '' +
        '<span class="wm-popup-eyebrow">' + escapeHtml((p.co || '') + (p.c ? ' · ' + p.c : '')) + '</span>' +
        '<h3 class="wm-popup-title">' + escapeHtml(p.t) + '</h3>' +
        fmt +
        '<a class="wm-pin-cta" href="' + escapeHtml(p.u) + '">View placement details →</a>';
      m.bindPopup(html, { maxWidth: 300, minWidth: 220, closeButton: true });
      m.on('popupopen', function (ev) { if (ev.target._icon) ev.target._icon.classList.add('is-active'); });
      m.on('popupclose', function (ev) { if (ev.target._icon) ev.target._icon.classList.remove('is-active'); });
      markers.push(m);
      if (p.c) ctx.cities[p.c] = true;
      if (p.co) ctx.countries[p.co] = true;
      ctx.count++;
    }
    if (markers.length) ctx.cluster.addLayers(markers); // bulk add — far faster at scale
  }

  function updateMeta(ctx, root) {
    var citiesEl = root.querySelector('[data-world-map-cities]');
    var prodsEl = root.querySelector('[data-world-map-products]');
    var countriesEl = root.querySelector('[data-world-map-countries]');
    if (citiesEl) citiesEl.textContent = Object.keys(ctx.cities).length;
    if (prodsEl) prodsEl.textContent = ctx.count;
    if (countriesEl) countriesEl.textContent = Object.keys(ctx.countries).length;

    // Refine any page-level "Cities" hero stat (e.g. the parent-OOH header) from
    // the full loaded dataset — the distinct-city total can't be computed
    // server-side past Shopify's 250-product paginate cap. Placements/countries
    // are already exact server-side, so we deliberately don't touch those.
    var cityStat = document.querySelector('[data-wm-stat="cities"]');
    if (cityStat) cityStat.textContent = Object.keys(ctx.cities).length.toLocaleString();
    var priceStat = document.querySelector('[data-wm-stat="price"]');
    if (priceStat && ctx.minPrice > 0) {
      priceStat.textContent = (ctx.minPrice / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }
  }

  function fitToData(ctx, root) {
    var bounds = ctx.cluster.getBounds();
    if (bounds && bounds.isValid()) {
      var country = root.dataset.country || '';
      var padOpts = country ? { padding: [40, 40], maxZoom: 9 } : { padding: [30, 30], maxZoom: 4 };
      ctx.map.fitBounds(bounds, padOpts);
      ctx.fitted = true;
    } else {
      ctx.map.setView([20, 20], 2);
    }
  }

  function firstPaint(ctx, root) {
    updateMeta(ctx, root);
    if (ctx.count === 0) {
      var fb = root.querySelector('[data-world-map-fallback] p');
      if (fb) fb.textContent = 'No locations to display.';
    }
    fitToData(ctx, root);
    setTimeout(function () { root.classList.add('is-ready'); }, 50);
  }

  function init() {
    var roots = document.querySelectorAll('[data-world-map]');
    roots.forEach(bootMap);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
